<script lang="ts" setup>
import type { SetOptional } from 'type-fest'

import SkrButton from '@/components/button/SkrButton.vue'
import SkrSwitch from '@/components/inputs/SkrSwitch.vue'
import TextInput from '@/components/inputs/TextInput.vue'
import OverlayBase from '@/components/overlays/OverlayBase.vue'
import SaveChangesElement from '@/components/SaveChangesElement.vue'
import SignatureCropper from '@/components/SignatureCropper.vue'
import type { MenuItem } from '@/components/SkrContextMenu.vue'
import SignatureImage from '@/components/visual-signature/SignatureImage.vue'
import VisualSignatureSkeleton from '@/components/visual-signature/VisualSignatureSkeleton.vue'
import SkrContextMenu from '@/components/SkrContextMenu.vue'
import ToolbarBase from '@/components/ToolbarBase.vue'

type SettingId = 'date' | 'claim' | 'standards'

const props = defineProps<{
  signature: SetOptional<VisualSignatureDetails, 'id'>
  operationType: 'create' | 'edit'
  isLoading: boolean
}>()

const emit = defineEmits<{
  action: [type: OverlayAction, data?: unknown]
  close: []
}>()

const visualSignatureStore = useVisualSignatureStore()
const { signatures } = storeToRefs(visualSignatureStore)

const { t } = useI18n()

const { quality: signatureQuality, legislation, provider } = useSigningDefaults()

const signatureInfo = reactive({
  alias: props.signature.alias,
  name: props.signature.name ?? '',
  optionalLine: props.signature.optionalLine ?? '',
  optionalLine2: props.signature.optionalLine2 ?? '',
})

const initialAlias = props.signature.alias

const usedAliases = computed(() => {
  return signatures.value.map(signature => signature.alias).filter(alias => alias !== initialAlias)
})

const aliasRules = computed(() => [
  (value: string) => Boolean(value) || t('profile.visual_signature.form.alias.error.required'),
  (value: string) => !usedAliases.value.includes(value) || t('profile.visual_signature.form.alias.error.unique'),
])

const switchValues = reactive<Record<SettingId, boolean>>({
  date: props.signature.displayDate ?? true,
  claim: props.signature.displayClaim ?? true,
  standards: props.signature.displayStandards ?? true,
})

const { isUploadImageFlag, isEditDateFlag, isEditClaimFlag, isEditStandardsFlag } = useVisualSignatureFlags()

const visualSignatureSettings = computed<Record<SettingId, { label: string; isVisible: boolean }>>(() => {
  return {
    date: {
      label: t('profile.visual_signature.form.date_label'),
      isVisible: isEditDateFlag.value,
    },
    claim: {
      label: t('profile.visual_signature.form.claim_label'),
      isVisible: isEditClaimFlag.value,
    },
    standards: {
      label: t('profile.visual_signature.form.standards'),
      isVisible: isEditStandardsFlag.value,
    },
  }
})

const { isStarterPlan } = storeToRefs(useBusinessStore())

const isVisualSignatureEditable = computed(() => {
  if (isStarterPlan.value) {
    return false
  }

  return isEditDateFlag.value || isEditClaimFlag.value || isEditStandardsFlag.value
})

const { language } = storeToRefs(useLanguageStore())

const visualSignatureParams = computed(() => {
  const params: Partial<VisualSignatureData> = {
    language: language.value,
    quality: signatureQuality.value,
    legislation: legislation.value,
    provider: provider.value,
    name: signatureInfo.name,
    optionalLine: signatureInfo.optionalLine,
    optionalLine2: signatureInfo.optionalLine2,
    displayClaim: switchValues.claim,
    displayDate: switchValues.date,
    displayStandards: switchValues.standards,
    image: editedImageData.value,
  }

  return params
})

const personalSignatureImage = ref<InstanceType<typeof SignatureImage>>()
const rerenderVisualSignature = () => {
  personalSignatureImage.value?.updateImage()
}

const copy = computed(() => {
  if (props.operationType === 'create') {
    return {
      headerTitle: t('profile.visual_signature.create.header.title'),
      title: t('profile.visual_signature.create.title'),
      saveAction: t('profile.visual_signature.create_signature'),
    }
  } else {
    return {
      headerTitle: props.signature.alias,
      title: t('profile.visual_signature.edit.title'),
      saveAction: t('business.settings.save'),
    }
  }
})

const editedSignature = computed<Omit<VisualSignatureDetails, 'id'>>(() => ({
  alias: signatureInfo.alias,
  name: signatureInfo.name,
  optionalLine: signatureInfo.optionalLine,
  optionalLine2: signatureInfo.optionalLine2,
  displayClaim: switchValues.claim,
  displayDate: switchValues.date,
  displayStandards: switchValues.standards,
}))

const isValid = ref(true)

const isDisplayClaimUnchanged = computed(() => {
  if (!isEditClaimFlag.value) {
    return true
  }
  return editedSignature.value.displayClaim === (props.signature.displayClaim ?? true)
})

const isDisplayDateUnchanged = computed(() => {
  if (!isEditDateFlag.value) {
    return true
  }
  return editedSignature.value.displayDate === (props.signature.displayDate ?? true)
})

const isDisplayStandardsUnchanged = computed(() => {
  if (!isEditStandardsFlag.value) {
    return true
  }
  return editedSignature.value.displayStandards === (props.signature.displayStandards ?? true)
})

/**
 * The previously associated custom image of the signature.
 */
const savedImageData = ref<CustomImageData | null>(null)

/**
 * The currently edited custom image of the signature. This can be a new image, the previously saved image or null.
 */
const editedImageData = ref<CustomImageData | null>(null)

const hasImageChanged = computed(() => {
  return editedImageData.value?.content !== savedImageData.value?.content
})

provide('imageData', editedImageData)

const hasUnsavedChanges = computed(() => {
  return !(
    props.signature.alias === editedSignature.value.alias &&
    props.signature.name === editedSignature.value.name &&
    props.signature.optionalLine === editedSignature.value.optionalLine &&
    props.signature.optionalLine2 === editedSignature.value.optionalLine2 &&
    isDisplayClaimUnchanged.value &&
    isDisplayDateUnchanged.value &&
    isDisplayStandardsUnchanged.value &&
    !hasImageChanged.value
  )
})

const { canLeave } = useOverlayNavigationTrap(hasUnsavedChanges, () => emit('close'))

const onClose = async () => {
  if (await canLeave()) emit('close')
}

/**
 * The preview always renders from the local image data, so in edit mode it has to wait
 * for the persisted image to be fetched to avoid an initial render without the image.
 */
const isPreviewReady = ref(props.operationType === 'create')

const fetchCustomImage = async () => {
  try {
    const imageData = await visualSignatureStore.getSignatureImage(props.signature.id!)
    // Set initial reference for unsaved changes comparison
    savedImageData.value = imageData
    if (!editedImageData.value) {
      editedImageData.value = imageData
    }
  } finally {
    isPreviewReady.value = true
  }
}

onMounted(() => {
  if (props.operationType === 'edit') {
    void fetchCustomImage()
  }
})

const saveCustomImage = (imageData: CustomImageData) => {
  editedImageData.value = imageData
  rerenderVisualSignature()
}

const removeCustomImage = () => {
  editedImageData.value = null
  rerenderVisualSignature()
}

const isUsingCustomImage = computed(() => {
  return Boolean(editedImageData.value)
})

const setAsDefault = async () => {
  if (await canLeave()) emit('action', 'select')
}

const deleteSignature = async () => {
  if (await canLeave()) emit('action', 'delete')
}

const contextMenuItems = computed<MenuItem[]>(() => [
  {
    label: t('visual_signature.item.set_default'),
    icon: 'custom:admin_guide',
    isHidden: props.signature.isDefault,
    action: setAsDefault,
  },
  {
    label: t('visual_signature.item.delete_signature'),
    icon: 'custom:minus_circle',
    color: 'danger',
    isHidden: signatures.value.length === 1,
    action: deleteSignature,
  },
])

const submit = () => {
  if (props.isLoading) return

  emit('action', 'complete', {
    updates: editedSignature.value,
    customImage: editedImageData.value,
    hasImageChanged: hasImageChanged.value,
  })
}

const form = ref<{ validate: () => void } | null>(null)

if (props.operationType === 'create') {
  const unwatchForm = watchEffect(() => {
    if (form.value) {
      form.value.validate()
      unwatchForm()
    }
  })
}
</script>

<template>
  <overlay-base class="overlay-edit-signature" :close-on-back="false">
    <template #header>
      <toolbar-base class="toolbar" :title="copy.headerTitle" :app="false" is-closeable @close="onClose">
        <template v-if="operationType === 'edit'" #options>
          <skr-context-menu :items="contextMenuItems" dense />
        </template>
      </toolbar-base>
    </template>
    <v-container class="container" fluid>
      <div class="content">
        <h1>{{ copy.title }}</h1>
        <v-container fluid>
          <v-row align="start">
            <v-col cols="12" lg="6" order="2" order-lg="1">
              <v-form ref="form" v-model="isValid">
                <section class="mb-4">
                  <div class="mb-4 | personal-signature__title shrink">
                    {{ t('profile.visual_signature.form.alias.title') }}
                  </div>
                  <text-input
                    v-model="signatureInfo.alias"
                    data-cy="alias_input"
                    :hint="t('profile.visual_signature.form.alias.hint')"
                    :rules="aliasRules"
                    persistent-hint
                  />
                </section>
                <section>
                  <div class="mb-4 | personal-signature__title shrink">
                    {{ t('profile.visual_signature.form.title') }}
                  </div>
                  <text-input
                    v-model="signatureInfo.name"
                    data-cy="name_input"
                    :label="t('profile.visual_signature.form.name_label')"
                    :hint="isUsingCustomImage ? t('profile.visual_signature.form.name_help') : ''"
                    :disabled="isUsingCustomImage"
                    persistent-hint
                  />
                  <text-input
                    v-model="signatureInfo.optionalLine"
                    class="mb-3"
                    data-cy="company_input"
                    :label="t('profile.visual_signature.form.optional_line.label')"
                    :hint="t('profile.visual_signature.form.optional_line.hint')"
                    persistent-hint
                  />
                  <text-input
                    v-model="signatureInfo.optionalLine2"
                    class="mb-3"
                    data-cy="location_input"
                    :label="t('profile.visual_signature.form.optional_line_2.label')"
                    :hint="t('profile.visual_signature.form.optional_line_2.hint')"
                    persistent-hint
                  />
                </section>
              </v-form>
              <template v-if="isUploadImageFlag">
                <div class="mt-6 | personal-signature__title shrink">
                  {{ t('profile.visual_signature.upload.title') }}
                </div>
                <div class="personal-signature__upload">
                  <p>
                    {{ t('profile.visual_signature.upload.text') }}
                  </p>
                  <signature-cropper @save="saveCustomImage" @remove="removeCustomImage" />
                </div>
              </template>
            </v-col>
            <v-col cols="12" lg="6" order="1" order-lg="2">
              <div class="personal-signature__title shrink">
                {{ t('profile.visual_signature.preview.title') }}
              </div>
              <div class="personal-signature__stage grow">
                <div class="personal-signature__signature-wrapper elevation-3">
                  <signature-image
                    v-if="isPreviewReady"
                    ref="personalSignatureImage"
                    :width="300"
                    :height="140"
                    :parameters="visualSignatureParams"
                    owned
                  />
                  <visual-signature-skeleton v-else :width="300" :height="140" />
                </div>
              </div>
              <template v-if="isVisualSignatureEditable">
                <div class="mt-6 | personal-signature__title shrink">
                  {{ t('profile.visual_signature.form.settings') }}
                </div>
                <div>
                  <template v-for="(setting, id) in visualSignatureSettings">
                    <v-row
                      v-if="setting.isVisible"
                      :key="id"
                      align="center"
                      justify="space-between"
                      class="personal-signature__settings"
                      no-gutters
                    >
                      <v-col>
                        <div class="d-flex align-center gap-2">
                          <div class="personal-signature__switch__label" :class="{ active: switchValues[id] }">
                            {{ setting.label }}
                          </div>
                        </div>
                      </v-col>
                      <v-col class="d-flex justify-end">
                        <skr-switch
                          v-model="switchValues[id]"
                          color="success"
                          :data-cy="`${id}_switch`"
                          :label="switchValues[id] ? t('global.form.switch.true') : t('global.form.switch.false')"
                          class="personal-signature__switch"
                        />
                      </v-col>
                    </v-row>
                  </template>
                </div>
              </template>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-container>
    <save-changes-element
      class="save-element"
      :has-unsaved-changes="hasUnsavedChanges"
      :is-loading="isLoading"
      :is-disabled="!isValid"
      :action-text="copy.saveAction"
      @save="submit"
    >
      <template v-if="operationType === 'create'">
        <div class="action">
          <skr-button data-cy="save_button" size="xl" :loading="isLoading" :disabled="!isValid" @click="submit">
            {{ t('profile.visual_signature.create_signature') }}
          </skr-button>
        </div>
      </template>
    </save-changes-element>
  </overlay-base>
</template>

<style lang="scss" scoped>
.toolbar {
  position: sticky;
  top: 0;
  :deep(.toolbar__options) {
    height: 100%;
    display: flex;
    align-items: center;
  }
  :deep(.menu-activator) {
    height: 100%;
    width: auto;
    color: $c-text;
    aspect-ratio: 1 / 1;
    border-radius: 0;
    &::before {
      background: $c-primary;
    }
  }
}

.container {
  height: calc(100% - 90px);
  overflow-y: auto;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: $site-width-narrow;
  padding: 8 * $bw;
  margin: 5rem auto 0;
  > h1 {
    line-height: 1.2;
    text-align: center;
    margin-bottom: 10 * $bw;
    @include h1;
  }
}

.personal-signature {
  &__title {
    font: bold 1.125rem/1.1 $averta;
    color: $c-text-alt;
    margin-bottom: 16px;
  }

  &__stage {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    margin: 16px 0 33px;
    padding: 34px 32px;
    border: 10px solid $c-white;
    border-radius: 3px;
    background-color: $c-grey-fine;
    @include skribble-box-shadow;
    @include maw(xs) {
      padding-top: 32px;
      padding-bottom: 32px;
    }
  }

  &__upload {
    padding: 16px 0;
  }

  &__settings {
    &:not(:first-of-type) {
      border-top: 1px solid $c-border;
    }
    :deep(.v-switch .v-selection-control) {
      min-height: inherit;
    }
  }

  &__switch {
    margin: 2 * $bw 0;
    &.v-input--is-label-active .v-label {
      color: $c-success;
    }

    .v-label {
      font: bold 1rem/1 $averta;
      color: $c-grey;
    }

    &__label {
      &.active {
        font-weight: bold;
        color: $c-text-alt;
      }
      &.disabled {
        color: $c-grey;
      }
    }
  }

  &__signature-wrapper {
    position: relative;
    left: 0;
    top: 0;
    display: flex;
    align-items: stretch;
    width: 300px;
    height: 140px;
    border-radius: 5px;
    background-color: $c-white;
    font-family: $averta, sans-serif;
    font-size: 1rem;
  }
}

.save-element {
  position: sticky;
  bottom: 0;
  > .action {
    height: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
